import React from 'react';
import { ArrowUpRight, Loader } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Renders the CTA button for a single billing plan card.
 *
 * Replaces a previously-nested ternary in Billing.js. Each branch is now a
 * named variant resolved by `resolveVariant()` and dispatched through the
 * `VARIANTS` lookup table — much easier to scan than `a ? b : c ? d : ...`.
 */
const VARIANT_FREE = 'free';
const VARIANT_RENEW = 'renew';
const VARIANT_UPGRADE = 'upgrade';
const VARIANT_DISABLED = 'disabled';

const resolveVariant = ({ plan, isCurrent, canUpgrade }) => {
  if (plan.price_usd === 0) return VARIANT_FREE;
  if (isCurrent) return VARIANT_RENEW;
  if (canUpgrade) return VARIANT_UPGRADE;
  return VARIANT_DISABLED;
};

const PlanCTA = ({ plan, isCurrent, canUpgrade, isAdmin, upgradingId, onUpgrade }) => {
  const variant = resolveVariant({ plan, isCurrent, canUpgrade });
  const isLoading = upgradingId === plan.id;
  const testId = `plan-cta-${plan.id}`;

  if (variant === VARIANT_FREE) {
    return (
      <Button
        disabled
        data-testid={testId}
        className="w-full bg-slate-100 text-slate-500 cursor-not-allowed rounded-sm"
      >
        {isCurrent ? 'Current Plan' : 'Free Forever'}
      </Button>
    );
  }

  if (variant === VARIANT_DISABLED) {
    return (
      <Button disabled data-testid={testId} className="w-full bg-slate-100 text-slate-500 cursor-not-allowed rounded-sm">
        —
      </Button>
    );
  }

  // RENEW + UPGRADE share the same button shell — only the label differs.
  const label = variant === VARIANT_RENEW
    ? <>Extend / Renew <ArrowUpRight size={14} className="ml-2" /></>
    : <>Upgrade to {plan.name} <ArrowUpRight size={14} className="ml-2" /></>;

  return (
    <Button
      onClick={() => onUpgrade(plan.id)}
      disabled={!isAdmin || isLoading}
      data-testid={testId}
      className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm hover:scale-[0.98] transition-transform"
    >
      {isLoading ? (
        <><Loader size={14} className="mr-2 animate-spin" /> Redirecting…</>
      ) : (
        label
      )}
    </Button>
  );
};

export default PlanCTA;
